import React from "react";
import axios from "axios";

import Layout from "../components/layout";
import SEO from "../components/seo";

import taxYear20212022Config from '../lib/salary-calculator/config/tax-year-2021-2022.json';
import taxYear20222023Config from '../lib/salary-calculator/config/tax-year-2022-2023.json';
import taxYear20222023JulyConfig from '../lib/salary-calculator/config/tax-year-2022-2023-july.json';
import TaxYear from '../lib/salary-calculator/tax-year/tax-year';
import Employee from '../lib/salary-calculator/employee';
import Salary from '../lib/salary-calculator/salary';
import UmbrellaNetIncomeCalculator from '../lib/salary-calculator/calculator/umbrella-net-income-calculator';

export default class IndexPage extends React.Component {

  state = {
    salary: "",
    salaryFreq: "hour",
    workingHours: "37.5",
    weeklyExpenses: "0",
    fullName: "",
    email: "",
    phone: "",
    recruiter: "",
    hasAcceptedTerms: false,
    showCalc: false,
    isLoading: false,
    error: ""
  }

  componentDidMount() {
    setTimeout(() => {
      window.addEventListener("resize", this.throttledSendMessage.bind(this));
      this.throttledSendMessage();
    }, 0);
  }

  componentDidUpdate() {
    this.throttledSendMessage();
  }

  onChange = (e) => {
    const name = e.target.name;
    this.setState({
      [name]: e.target.value
    });
  }

  sendMessage = function (e) {
    window.parent.postMessage(document.body.scrollHeight, '*');
    this.preventDefault(e);
  }

  preventDefault = function (e) {
    if(e && e.preventDefault) {
      e.preventDefault();
    } else {
      return false;
    }
  }

  throttledSendMessage = function (e) {
    this.debounce(this.sendMessage(e), 150);
  }

  debounce = function (fn, delay) {
    var timer = null;
    return function () {
      var context = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  onCalculate = async (e) => {
    e.preventDefault();

    if (this.state.isLoading) {
      return;
    }

    if (this.state.salary < 1) {
      return this.setState({
        error: 'Please provide your hour/day rate'
      });
    }

    if (this.state.salaryFreq !== 'hour' && this.state.salaryFreq !== 'day') {
      return this.setState({
        error: 'Please select your rate frequency (day or hour)'
      });
    }

    if (this.state.workingHours < 1 || this.state.workingHours > 60) {
      return this.setState({
        error: 'Please provide your number of working hours'
      });
    }

    if (this.state.fullName === '') {
      return this.setState({
        error: 'Please provide your full name'
      });
    }

    if (this.state.recruiter === '') {
      return this.setState({
        error: 'Please provide your recruitment agency name'
      });
    }

    if (this.state.email === '') {
      return this.setState({
        error: 'Please provide a valid email address'
      });
    }

    if (this.state.phone === '') {
      return this.setState({
        error: 'Please provide a phone number'
      });
    }

    if (!this.state.hasAcceptedTerms) {
      return this.setState({
        error: 'Please agree to the terms & conditions'
      });
    }

    this.setState({
      isLoading: true,
      showCalc: false,
      error: ''
    });

    try {
      await axios.post(
        `${process.env.GATSBY_API_HOST}/calculation`,
        {
          full_name: this.state.fullName,
          email: this.state.email,
          phone: this.state.phone,
          rate: this.state.salary,
          frequency: this.state.salaryFreq,
          hours: this.state.workingHours,
          expenses: this.state.weeklyExpenses,
          recruiter: this.state.recruiter,
          estimate: this.calc()
        }
      );

      this.setState({
        isLoading: false,
        showCalc: true
      });
    } catch (e) {
      let errorMessage = 'There was a problem submitting your information. Please contact our team directly.'

      if (e.response && e.response.data.error) {
        errorMessage = e.response.data.error;
      }

      this.setState({
        isLoading: false,
        showCalc: false,
        error: errorMessage
      });
    }
  }

  calc = () => {
    const today = new Date();
    let dynamicTaxYear;

    if (today.toISOString() > '2022-07-06T00:00:00.000Z') {
      dynamicTaxYear = taxYear20222023JulyConfig;
    } else if (today.toISOString() > '2022-04-06T00:00:00.000Z') {
      dynamicTaxYear = taxYear20222023Config;
    } else {
      dynamicTaxYear = taxYear20212022Config;
    }

    const taxYear = new TaxYear(dynamicTaxYear);
    const employee = new Employee({
      hoursPerDay: this.state.workingHours / 5
    });
    const salary = new Salary({
      salary: this.state.salary,
      employee: employee,
      frequency: this.state.salaryFreq,
      annualExpense: this.state.weeklyExpenses * 52
    });

    const umbrellaMargin = 18;
    const annualUmbrellaMargin = umbrellaMargin * 52;
    const netIncomeCalculator = new UmbrellaNetIncomeCalculator(
      annualUmbrellaMargin,
      taxYear
    );

    return (netIncomeCalculator.calculate(employee, salary) / 52).toFixed(2);
  }

  onBack = () => {
    this.setState({
      showCalc: false,
      error: ''
    });
  }

  render() {
    return <Layout>
      <SEO title="Take home pay calculator tool" />

      {this.state.showCalc && (
        <div className="calculation">
          <p>Your estimated take home pay is:</p>
          <p className="take-home-pay">£{this.calc()}</p>
          <p className="take-home-pay-freq">Per week</p>
          <div>
            <p>Thank you for your submission.</p>
            <p>A specialist from our team will be in touch to discuss your calculation.</p>
            <button type="submit" className="btn-back" onClick={this.onBack} disabled={this.state.isLoading}>
              Back
            </button>
          </div>
          <p className="smallprint">The calculations are for illustration purposes only and are based on a series of <a href="https://www.unifiedpayroll.co.uk/terms-conditions" target="_blank" rel="noopener noreferrer">assumptions</a>.</p>
        </div>
      )}

      {!this.state.showCalc && (
        <form onSubmit={this.onCalculate}>
          <div className="form">
            <div className="form-item">
              <label htmlFor="salary">Your rate (£)</label>
              <div style={{ position: 'relative' }}>
                <input id="salary" className="form-item__input" type="text" name="salary" placeholder="0"  value={this.state.salary} onChange={this.onChange} />
                <select autoComplete="new-password" name="salaryFreq" value={this.state.salaryFreq} onChange={this.onChange} className="form-item__salary-freq">
                  <option value="hour">Per hour</option>
                  <option value="day">Per day</option>
                </select>
              </div>
            </div>
            <div className="form-item">
              <label htmlFor="working-hours">Working hours / week</label>
              <input className="form-item__input" type="text" name="workingHours" id="working-hours" placeholder="37.5" onChange={this.onChange} value={this.state.workingHours} />
            </div>
            <div className="form-item">
              <label htmlFor="weekly-expenses">Expenses / week (£)</label>
              <input className="form-item__input" type="text" name="weeklyExpenses" id="weekly-expenses" placeholder="0"  onChange={this.onChange} value={this.state.weeklyExpenses} />
            </div>
            <div className="form-item">
              <label htmlFor="name">Full name</label>
              <input className="form-item__input" type="text" name="fullName" id="name" placeholder="" onChange={this.onChange} value={this.state.fullName} />
            </div>
            <div className="form-item">
              <label htmlFor="email">Email</label>
              <input className="form-item__input" type="email" name="email" id="email" placeholder=""  onChange={this.onChange} value={this.state.email} />
            </div>
            <div className="form-item">
              <label htmlFor="phone">Phone</label>
              <input className="form-item__input" type="tel" name="phone" id="phone" placeholder=""  onChange={this.onChange} value={this.state.phone} />
            </div>
            <div className="form-item" style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto' }}>
              <label htmlFor="recruiter">Recruitment agency</label>
              <input className="form-item__input" type="text" name="recruiter" id="recruiter" placeholder="" onChange={this.onChange} value={this.state.recruiter} />
            </div>
          </div>
          <div className="terms-label">
            <input id="terms" type="checkbox" checked={this.state.hasAcceptedTerms} onChange={() => {
              this.setState({
                hasAcceptedTerms: !this.state.hasAcceptedTerms
              })
            }} />
            <label htmlFor="terms">
              I agree to <a href="https://www.unifiedpayroll.co.uk/terms-conditions" target="_blank" rel="noopener noreferrer">terms &amp; conditions</a>
            </label>
          </div>
          {this.state.error !== '' && (
            <div className="form-error">
              <p>{this.state.error}</p>
            </div>
          )}
          <div style={{ padding: '30px 0'}}>
            <button type="submit" className="btn-calculate">
              {this.state.isLoading && (
                <>
                  <div id="floatingCirclesG">
                    <div className="f_circleG" id="frotateG_01"></div>
                    <div className="f_circleG" id="frotateG_02"></div>
                    <div className="f_circleG" id="frotateG_03"></div>
                    <div className="f_circleG" id="frotateG_04"></div>
                    <div className="f_circleG" id="frotateG_05"></div>
                    <div className="f_circleG" id="frotateG_06"></div>
                    <div className="f_circleG" id="frotateG_07"></div>
                    <div className="f_circleG" id="frotateG_08"></div>
                  </div>
                  <span> Loading...</span>
                </>
              )}
              {!this.state.isLoading && (
                <>Calculate</>
              )}
            </button>
          </div>
        </form>
      )}

    </Layout>
  }
}
